"use client";

import { motion } from "framer-motion";

const METRICS = [
  {
    value: "3×",
    label: "More inbound leads captured",
    sub: "vs. voicemail or missed calls",
    color: "from-violet-400 to-purple-400",
  },
  {
    value: "0",
    label: "Calls missed after hours",
    sub: "including weekends & holidays",
    color: "from-cyan-400 to-blue-400",
  },
  {
    value: "40%",
    label: "Reduction in front desk load",
    sub: "routine calls handled automatically",
    color: "from-emerald-400 to-teal-400",
  },
  {
    value: "2 wks",
    label: "Average time to deploy",
    sub: "from kickoff to live calls",
    color: "from-amber-400 to-orange-400",
  },
];

const TESTIMONIAL = {
  quote: "We used to miss 30% of our weekend calls — people would just go with whoever answered first. Since deploying the voice agent, we haven't missed a call in four months. The ROI was obvious in the first week.",
  name: "Operations Manager",
  company: "6-location dental group, Texas",
  initials: "DG",
};

export default function VoiceAgentResults() {
  return (
    <section className="bg-zinc-950 py-24 border-t border-white/[0.04]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        <div className="text-center mb-16">
          <p className="text-sm font-semibold text-violet-400 uppercase tracking-widest mb-3">Results</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            What clients see in the first 30 days
          </h2>
          <p className="text-zinc-400 text-lg max-w-xl mx-auto">
            Measurable outcomes from day one — not months of tuning and configuration.
          </p>
        </div>

        {/* Metrics grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
          {METRICS.map((metric, i) => (
            <motion.div
              key={metric.label}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.45, delay: i * 0.08 }}
              className="bg-white/[0.03] border border-white/[0.07] rounded-2xl p-6 text-center hover:border-white/12 hover:bg-white/[0.05] transition-all group"
            >
              <div className={`text-4xl font-bold bg-gradient-to-r ${metric.color} bg-clip-text text-transparent mb-2 tabular-nums`}>
                {metric.value}
              </div>
              <div className="text-sm font-semibold text-white mb-1">{metric.label}</div>
              <div className="text-xs text-zinc-500">{metric.sub}</div>
            </motion.div>
          ))}
        </div>

        {/* Testimonial */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.5 }}
          className="bg-gradient-to-br from-violet-500/10 to-cyan-500/5 border border-violet-500/20 rounded-2xl p-8 lg:p-10"
        >
          <div className="flex gap-1 mb-5">
            {Array.from({ length: 5 }).map((_, i) => (
              <svg key={i} className="w-4 h-4 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 0 0 .95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 0 0-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 0 0-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 0 0-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 0 0 .951-.69l1.07-3.292Z" />
              </svg>
            ))}
          </div>
          <p className="text-zinc-200 text-lg lg:text-xl leading-relaxed mb-6 max-w-3xl">
            &ldquo;{TESTIMONIAL.quote}&rdquo;
          </p>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-600 to-cyan-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
              {TESTIMONIAL.initials}
            </div>
            <div>
              <div className="text-sm font-semibold text-white">{TESTIMONIAL.name}</div>
              <div className="text-xs text-zinc-500">{TESTIMONIAL.company}</div>
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
}
